import { useRef, useEffect } from 'react';

import { Product } from '../../../../types/api/products/Product';
import { useAuthContext } from '../../../../data/context/authContext';
import dispatchGoogleAnalyticsEvent, { gaEvents, gaFormatters } from '../../../../modules/analytics/google/dispatchGoogleAnalyticsEvent';
import useNextDelivery from '../../TreatmentSelector/Incentives/hooks/useNextDelivery/useNextDelivery';
import useAffiliateCheckedHook from '../../../../modules/affiliate/hooks/useAffiliateCheckedHook';

/**
 * Runs once on page load to send a google analytics request. This should be run on treatment pages only.
 */
const useTreatmentPageSelectorAnalytics = (treatment?: Product) => {
    const { isLoggedIn } = useAuthContext();
    const { nextDeliveryDate, cutOffTime } = useNextDelivery();

    // Need to wait for affiliate check to run as the analytics gaFormatters.productPage uses it.
    const hasAffiliateBeenChecked = useAffiliateCheckedHook();

    const hasAnalyticsRun = useRef(false);

    useEffect(() => {
        if (hasAnalyticsRun.current === true) {
            return;
        }

        const deliveryData = (nextDeliveryDate && cutOffTime) ? {
            'cutoff-time': nextDeliveryDate,
            'next-delivery-date': cutOffTime,
        } : null;

        if (treatment && deliveryData && hasAffiliateBeenChecked) {
            const getTreatmentImage = () => (
                Array.isArray(treatment.public) === false && treatment.public.images.featuredUrl
                    ? treatment.public.images.featuredUrl
                    : ''
            );

            const getTreatmentDescription = () => (
                Array.isArray(treatment.public) === false && treatment.public.description
                    ? treatment.public.description
                    : ''
            );

            dispatchGoogleAnalyticsEvent(
                gaEvents.ProductPage,
                gaFormatters.productPage(treatment, deliveryData, isLoggedIn, getTreatmentImage, getTreatmentDescription)
            );

            hasAnalyticsRun.current = true;
        }
    }, [treatment, nextDeliveryDate, cutOffTime, isLoggedIn, hasAffiliateBeenChecked]);
};

export default useTreatmentPageSelectorAnalytics;
